import { useCallback, useEffect, useState } from 'react';
import { api, money, pct, type OpportunityEpisode, type Trade } from '../lib/api.ts';

/**
 * Trade log and opportunity history (FR-4.4).
 *
 * Two tabs on one screen: what was executed, and what was merely detected.
 * The second is the more useful one right now — until execution ships, the
 * opportunity history is the entire evidence base for whether this tool has an
 * edge, and its lifetime column is the honest answer to whether a human could
 * have acted on any of it.
 */

type Tab = 'trades' | 'opportunities';

const STATUS_CLASS: Record<Trade['status'], string> = {
  SUCCESS: 'pill ok',
  PARTIAL: 'pill warn',
  FAILED: 'pill bad',
  REJECTED: 'pill',
  PENDING: 'pill warn',
};

export function TradeLog({ quoteCurrency }: { quoteCurrency: string }) {
  const [tab, setTab] = useState<Tab>('trades');
  const [trades, setTrades] = useState<Trade[]>([]);
  const [summary, setSummary] = useState<{
    successfulTrades: number;
    totalNetPnl: number;
    averageNetPnl: number;
    partials: number;
    counts: Record<string, number>;
  } | null>(null);
  const [episodes, setEpisodes] = useState<OpportunityEpisode[]>([]);
  const [stats, setStats] = useState<{ count: number; perHour: number; bestNetPct: number; avgNetPct: number } | null>(
    null,
  );
  const [hours, setHours] = useState(24);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [tradeData, historyData] = await Promise.all([api.trades(), api.history(hours)]);
      setTrades(tradeData.trades);
      setSummary(tradeData.summary);
      setEpisodes(historyData.episodes);
      setStats(historyData.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load history');
    }
  }, [hours]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Trade log</h1>
          <div className="sub">
            Every attempt is recorded — successes, partials, failures and rejections alike.
          </div>
        </div>
        <div className="row">
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} style={{ width: 140 }}>
            <option value={1}>last hour</option>
            <option value={24}>last 24 hours</option>
            <option value={168}>last 7 days</option>
          </select>
          <button className="btn small" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="notice bad">{error}</div>}

      <div className="grid cols-4">
        <div className="card stat">
          <div className="label">Realized P&amp;L</div>
          <div className={`value ${(summary?.totalNetPnl ?? 0) >= 0 ? 'green' : 'red'}`}>
            {money(summary?.totalNetPnl ?? 0)} <span className="dim" style={{ fontSize: 13 }}>{quoteCurrency}</span>
          </div>
          <div className="hint">across {summary?.successfulTrades ?? 0} completed trades</div>
        </div>
        <div className="card stat">
          <div className="label">Unhedged partials</div>
          <div className={`value ${(summary?.partials ?? 0) > 0 ? 'amber' : ''}`}>{summary?.partials ?? 0}</div>
          <div className="hint">
            {(summary?.partials ?? 0) > 0 ? 'one leg filled — needs manual resolution' : 'no open exposure'}
          </div>
        </div>
        <div className="card stat">
          <div className="label">Opportunities detected</div>
          <div className="value">{stats?.count ?? 0}</div>
          <div className="hint">{(stats?.perHour ?? 0).toFixed(1)} per hour</div>
        </div>
        <div className="card stat">
          <div className="label">Best net seen</div>
          <div className="value green">{pct(stats?.bestNetPct ?? 0, 2)}</div>
          <div className="hint">avg {pct(stats?.avgNetPct ?? 0, 2)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="nav">
            <button className={tab === 'trades' ? 'active' : ''} onClick={() => setTab('trades')}>
              Executed trades ({trades.length})
            </button>
            <button
              className={tab === 'opportunities' ? 'active' : ''}
              onClick={() => setTab('opportunities')}
            >
              Detected opportunities ({episodes.length})
            </button>
          </div>
        </div>

        {tab === 'trades' ? (
          trades.length === 0 ? (
            <div className="empty">
              <strong>No trades yet.</strong>
              One-click execution is the Week 4 deliverable. Until then this screen stays empty and
              the opportunity tab carries the evidence.
            </div>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Pair</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th className="num">Size</th>
                    <th className="num">Buy fill</th>
                    <th className="num">Sell fill</th>
                    <th className="num">Net P&amp;L</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t) => (
                    <tr key={t.id}>
                      <td className="faint">{new Date(t.createdAt).toLocaleString()}</td>
                      <td className="sym">{t.symbol}</td>
                      <td className="dim">
                        {t.buyExchange}→{t.sellExchange}
                      </td>
                      <td>
                        <span className={STATUS_CLASS[t.status]}>{t.status.toLowerCase()}</span>
                      </td>
                      <td className="num">{money(Number(t.sizeQuote), 0)}</td>
                      <td className="num">{t.buyFillPrice ? money(Number(t.buyFillPrice), 4) : '—'}</td>
                      <td className="num">{t.sellFillPrice ? money(Number(t.sellFillPrice), 4) : '—'}</td>
                      <td className={`num ${Number(t.netPnl ?? 0) >= 0 ? 'green' : 'red'}`}>
                        {t.netPnl ? money(Number(t.netPnl)) : '—'}
                      </td>
                      <td className="faint" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.failureReason ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : episodes.length === 0 ? (
          <div className="empty">
            <strong>No opportunities detected in this window.</strong>
            At the current threshold, spreads did not clear both taker fees plus the buffer.
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Detected</th>
                    <th>Pair</th>
                    <th>Route</th>
                    <th className="num">Gross</th>
                    <th className="num">Net</th>
                    <th className="num">Peak</th>
                    <th className="num">Lifetime</th>
                    <th>Alerted</th>
                  </tr>
                </thead>
                <tbody>
                  {episodes.map((e) => {
                    const lifetimeMs = e.expiredAt
                      ? new Date(e.expiredAt).getTime() - new Date(e.detectedAt).getTime()
                      : null;
                    return (
                      <tr key={e.id}>
                        <td className="faint">{new Date(e.detectedAt).toLocaleTimeString()}</td>
                        <td className="sym">{e.symbol}</td>
                        <td className="dim">
                          {e.buyExchange}→{e.sellExchange}
                        </td>
                        <td className="num dim">{pct(e.grossPct)}</td>
                        <td className="num">{pct(e.netPct)}</td>
                        <td className="num green">{pct(e.peakNetPct)}</td>
                        <td className={`num ${lifetimeMs !== null && lifetimeMs < 1000 ? 'amber' : ''}`}>
                          {lifetimeMs === null ? 'open' : `${(lifetimeMs / 1000).toFixed(1)}s`}
                        </td>
                        <td className="faint">{e.alertedAt ? 'yes' : 'no'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="card-body faint" style={{ fontSize: 12, lineHeight: 1.6, borderTop: '1px solid var(--border)' }}>
              <strong>Lifetime</strong> is how long the gap stayed above threshold. Anything under a
              second is shorter than the time it takes to read an alert and confirm a trade — those
              opportunities are real, but not capturable by hand. That gap is the argument for the
              Phase 2 automated execution module.
            </div>
          </>
        )}
      </div>
    </>
  );
}
